import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaCheck, FaArrowRight } from 'react-icons/fa';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ServiceCrossNav from '../components/ServiceCrossNav';
import './ServicePage.css';

const Conseil = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO
        title="Conseil & Accompagnement Immobilier au Maroc | ATAA REALTY"
        description="Conseil immobilier au Maroc : cartographies des risques, scénarios de valorisation, audits, accompagnement en investissement et transaction immobilière. ATAA REALTY, Casablanca."
        canonical="https://ataarealty.com/services/conseil"
        keywords="conseil immobilier Maroc, accompagnement immobilier, audit immobilier Casablanca, cartographie risques immobiliers, valorisation actifs immobiliers, conseil investissement immobilier"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Conseil & Accompagnement Immobilier',
          provider: { '@type': 'ProfessionalService', name: 'ATAA REALTY', url: 'https://ataarealty.com/' },
          areaServed: { '@type': 'Country', name: 'Maroc' },
          description: "Conseil immobilier méthodique : analyse de problématiques juridiques, fiscales et opérationnelles, audits, accompagnement en investissement et transaction.",
          serviceType: 'Conseil immobilier, accompagnement immobilier',
          url: 'https://ataarealty.com/services/conseil'
        }}
      />
      <Navbar />

      {/* HERO */}
      <header className="svc-header svc-bg-conseil">
        <div className="svc-overlay"></div>
        <div className="container svc-title-box svc-anim-up">
          <span className="svc-eyebrow">Accompagnement</span>
          <h1>Conseil &amp; Accompagnement</h1>
        </div>
      </header>

      <section className="svc-content">
        <div className="container">
          {/* INTRO */}
          <div className="svc-section svc-anim-up svc-delay-1">
            <p className="svc-lead">
              Le conseil immobilier est envisagé comme un espace de lecture et de
              compréhension des situations, destiné à apporter de la clarté dans
              des contextes où les enjeux patrimoniaux, juridiques, fiscaux et
              économiques se croisent.
            </p>

            <p>
              Chez <strong>ATAA REALTY</strong>, cette mission prend la forme
              d'un accompagnement méthodique et attentif, fondé sur l'écoute des
              objectifs, l'examen des cadres applicables et la mise en perspective
              des options possibles, dans le respect des contraintes propres à
              chaque cas.
            </p>
          </div>

          {/* CHAMP D'INTERVENTION */}
          <div className="svc-section svc-anim-up svc-delay-2">
            <h2 className="svc-section-title">Champ d'intervention</h2>
            <ul className="svc-checklist">
              <li>
                <FaCheck />
                <span>
                  <strong>Analyse de problématiques immobilières et foncières</strong>{' '}
                  à caractère juridique, réglementaire, fiscal ou opérationnel.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Cartographies des risques immobiliers et fonciers</strong>{' '}
                  : identification, qualification et hiérarchisation des risques
                  juridiques, urbanistiques, fiscaux, techniques, financiers et
                  opérationnels, avec mesures d'atténuation.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Scénarios de valorisation et d'arbitrage d'actifs</strong>,
                  dans une logique de gestion patrimoniale ou institutionnelle.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Audits immobiliers et fonciers ciblés</strong>{' '}
                  en amont d'opérations d'investissement, de cession ou de
                  restructuration.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Accompagnement en investissement immobilier</strong> :
                  définition de stratégies, structuration des projets et
                  montages, assistance aux démarches opérationnelles.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Accompagnement en transaction immobilière</strong>, en
                  appui à la préparation, à la structuration et à la
                  sécurisation des processus de décision.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Assistance dans les relations avec l'administration fiscale</strong>{' '}
                  : procédures de redressement ou de régularisation.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Production de rapports de conseil</strong>, conduite
                  de séances de travail et consultations sur des situations
                  immobilières.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  <strong>Examen de situations spécifiques ou atypiques</strong>{' '}
                  relevant de l'immobilier, du foncier ou de la structuration
                  patrimoniale.
                </span>
              </li>
            </ul>
          </div>

          {/* CONTEXTES D'USAGE */}
          <div className="svc-section svc-anim-up svc-delay-3">
            <h2 className="svc-section-title">Contextes d'usage</h2>
            <p className="svc-section-intro">
              Les rapports établis sont destinés à éclairer des situations telles
              que :
            </p>
            <ul className="svc-checklist">
              <li>
                <FaCheck />
                <span>Projets d'investissement ou de développement immobilier.</span>
              </li>
              <li>
                <FaCheck />
                <span>
                  Opérations de réorganisation patrimoniale ou de restructuration
                  d'actifs.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  Processus de décision liés à des opérations de cession,
                  d'acquisition ou de partenariat.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  Situations impliquant des enjeux réglementaires, fiscaux ou
                  institutionnels.
                </span>
              </li>
              <li>
                <FaCheck />
                <span>
                  Démarches nécessitant une lecture indépendante et structurée
                  des options disponibles.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="svc-cta-section">
        <h3>Besoin d'un accompagnement structuré ?</h3>
        <p>
          Échangeons sur votre situation et identifions ensemble les leviers
          juridiques, fiscaux et opérationnels les plus adaptés à votre projet.
        </p>
        <Link to="/contact" className="svc-cta-btn">
          Discuter avec nos experts <FaArrowRight />
        </Link>
      </section>

      <ServiceCrossNav currentSlug="conseil" />

      <Footer />
    </>
  );
};

export default Conseil;
